import { hideCurrentModal } from "common/utils/ui/modal";
import "./modal-wrapper.scss";

// Elements that can receive keyboard focus inside an open modal.
const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])'
].join(",");

// Counter for generated heading ids so aria-labelledby stays unique across modals.
let labelIdCounter = 0;

// An element is focusable only if neither it nor any ancestor is hidden — a
// focusable inside a collapsed (display:none / hidden) section must stay out of
// the tab cycle, so we walk up the tree rather than inspecting the element alone.
const isVisible = element => {
  for (let node = element; node instanceof Element; node = node.parentElement) {
    if (node.hidden || node.getAttribute("aria-hidden") === "true") return false;
    const style = window.getComputedStyle(node);
    if (style.display === "none" || style.visibility === "hidden") return false;
  }
  return true;
};

const focusableElements = modal =>
  Array.from(modal.querySelectorAll(FOCUSABLE_SELECTOR)).filter(isVisible);

// Keep Tab / Shift+Tab cycling within the modal so focus never escapes it.
const trapFocus = modal => event => {
  if (event.key !== "Tab") return;

  const focusable = focusableElements(modal);
  if (focusable.length === 0) {
    event.preventDefault();
    return;
  }

  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  const active = document.activeElement;
  const outsideModal = !modal.contains(active);

  if (event.shiftKey) {
    if (active === first || outsideModal) {
      event.preventDefault();
      last.focus();
    }
  } else if (active === last || outsideModal) {
    event.preventDefault();
    first.focus();
  }
};

// Expose the dialog's heading as its accessible name when one is present.
const wireAccessibleName = modal => {
  // A titleless consumer can still name its dialog by supplying an explicit
  // aria-label / aria-labelledby in the ERB (e.g. the design-upload modal).
  // Honor that instead of clobbering it with a blank heading.
  if (modal.getAttribute("aria-label") || modal.getAttribute("aria-labelledby")) return;

  const title = modal.querySelector(".js-modal-wrapper-title");
  if (!title || !title.textContent.trim()) return;

  if (!title.id) {
    labelIdCounter += 1;
    title.id = `modal-wrapper-title-${labelIdCounter}`;
  }
  modal.setAttribute("aria-labelledby", title.id);
};

// Focus management for every modal-wrapper modal: trap focus while open, return
// focus to the invoking element on close, and announce the dialog to assistive
// tech. Delegated on document so it covers all consumers with one wiring.
const initFocusManagement = () => {
  const triggers = new WeakMap();
  const handlers = new WeakMap();

  $(document)
    .off(".modalWrapperA11y")
    .on("show.bs.modal.modalWrapperA11y", ".modal-wrapper", event => {
      const modal = event.currentTarget;
      // Bootstrap has not moved focus yet, so activeElement is still the trigger.
      triggers.set(modal, event.relatedTarget || document.activeElement);
      // Name the dialog on show, before any consumer's shown handler focuses inside.
      wireAccessibleName(modal);
    })
    .on("shown.bs.modal.modalWrapperA11y", ".modal-wrapper", event => {
      const modal = event.currentTarget;
      const handler = trapFocus(modal);
      handlers.set(modal, handler);
      modal.addEventListener("keydown", handler);

      // Only take focus if a consumer's own shown.bs.modal handler hasn't already
      // placed it inside the modal. Direct handlers on the modal element run before
      // this document-delegated one, so e.g. DeliverToModalClass focusing its zip
      // input would otherwise be clobbered by yanking focus back to Close.
      if (!modal.contains(document.activeElement)) {
        const [firstFocusable] = focusableElements(modal);
        (firstFocusable || modal).focus();
      }
    })
    .on("hidden.bs.modal.modalWrapperA11y", ".modal-wrapper", event => {
      const modal = event.currentTarget;

      const handler = handlers.get(modal);
      if (handler) {
        modal.removeEventListener("keydown", handler);
        handlers.delete(modal);
      }

      const trigger = triggers.get(modal);
      triggers.delete(modal);
      if (trigger && typeof trigger.focus === "function" && document.contains(trigger)) {
        trigger.focus();
      }
    });
};

export default () => {
  document.addEventListener("keydown", event => {
    if (event.key === "Escape") {
      const isNotCombinedKey = !(event.ctrlKey || event.altKey || event.shiftKey);
      if (isNotCombinedKey) {
        hideCurrentModal();
      }
    }
  });

  initFocusManagement();
};
